import clsx from "clsx";
import format from "date-fns/format";
import formatISO from "date-fns/formatISO";
import parseISO from "date-fns/parseISO";
import React, { useEffect, useState } from "react";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import CancelIcon from "@material-ui/icons/Cancel";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import ReorderIcon from "@material-ui/icons/Reorder";
import SearchIcon from "@material-ui/icons/Search";
import InvalidIcon from "@material-ui/icons/Warning";

import { DASH, EMPTY_STRING } from "common/constants";
import CalendarAdd from "components/Icons/CalendarAdd";
import Loader from "components/Loader";
import SummaryInfo from "components/SummaryInfo";
import { useRacehorse360Api } from "hooks/api";
import { useDebouncedNameValue } from "hooks/useSearch";
import { STABLE_ENTRIES_INPUT } from "../constants";
import { STABLE_ENTRIES_ERROR } from "../validationSchema";
import AddUnlistedHorsePopup from "./AddUnlistedHorsePopup";
import { UnlistedHorse } from "./AddUnlistedHorsePopup/AddUnlistedHorsePopup";
import DatePopup from "./DatePopup";

import useStyles from "./styles";

interface Props {
  stallApplication: racehorse360.IStallApplication;
  isListStableEntriesFetching: boolean;
  onChange: (stableEntries: racehorse360.IStableEntry[]) => void;
  originalStableEntries?: racehorse360.IStableEntry[];
  pickedStableEntries?: racehorse360.IStableEntry[];
  hasEntriesValidationError: boolean;
}

type StableEntriesMap = Record<string, racehorse360.IStableEntry>;

const mapStablingEntries = (
  stablingEntries: racehorse360.IStableEntry[] = []
): StableEntriesMap => {
  return (
    stablingEntries.reduce((acc, cur) => {
      const horseId = cur.horseId || EMPTY_STRING;
      return {
        ...acc,
        [horseId]: { ...cur, horseId }
      } as StableEntriesMap;
    }, {} as StableEntriesMap) || {}
  );
};

const StablingEntries = (props: Props) => {
  const {
    stallApplication,
    isListStableEntriesFetching,
    onChange,
    originalStableEntries = [],
    pickedStableEntries = [],
    hasEntriesValidationError
  } = props;
  const classes = useStyles();

  const [stateStablingEntries, setStateStablingEntries] =
    useState<StableEntriesMap>(
      mapStablingEntries(pickedStableEntries || originalStableEntries)
    );
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showResults, setShowResults] = useState<boolean>(false);
  const [isDatePopupOpen, setIsDatePopupOpen] = useState<boolean>(false);
  const [isCollapseMode, setIsCollapseMode] = useState<boolean>(false);
  const [activeStablingEntry, setActiveStablingEntry] =
    useState<racehorse360.IStableEntry>();
  const [isOpenEntryModal, setIsOpenEntryModal] = useState<boolean>(false);
  const debouncedSearchQuery = useDebouncedNameValue(searchQuery, {
    timeout: 500,
    startFrom: 2
  });
  const { usePullHorsesShortInfo } = useRacehorse360Api();
  const stablingEntries = Object.values(stateStablingEntries).filter(Boolean);

  const { data, isLoading } = usePullHorsesShortInfo(
    {
      query: {
        filterString: { similarOrContains: debouncedSearchQuery },
        filterBy: ["horseName", "horseRegistrationNumber", "horseChipNumber"]
      },
      pagingOptions: {
        maxResults: 100
      },
      getOptions: {
        select: [
          "id",
          "horseName",
          "horseRegistrationNumber",
          "breederName",
          "ownerName"
        ]
      }
    },
    {
      enabled: debouncedSearchQuery?.length > 2
    }
  );

  const horses = data?.horsesShortInfo || [];

  const toggleDatePopupOpen = () => {
    setIsDatePopupOpen(!isDatePopupOpen);
  };

  const onAddHorseClick = (horse: UnlistedHorse) => {
    const newStateStablingEntries = {
      ...stateStablingEntries,
      [horse.id]: {
        stallApplicationId: stallApplication?.id,
        horseId: horse.id,
        customHorseName: horse.name,
        customHorseRegistrationNumber: horse.registrationNumber
      }
    };
    setStateStablingEntries(newStateStablingEntries);
    setIsOpenEntryModal(false);
    setSearchQuery("");
    setShowResults(false);
    onChange(Object.values(newStateStablingEntries).filter(Boolean));
  };

  const handleSearchQueryChange = event => {
    const value = event.target.value;
    setSearchQuery(value);
  };

  const handleSearchFocus = () => {
    setShowResults(true);
  };

  const handleHorseClick =
    (horseShortInfo: racehorse360.IHorseShortInfo) => () => {
      const newValue = {
        ...stateStablingEntries,
        [horseShortInfo.id]: {
          stallApplicationId: stallApplication?.id,
          horseId: horseShortInfo.id,
          horse: {
            id: horseShortInfo.id,
            name: horseShortInfo.horseName,
            registrationNumber: horseShortInfo.horseRegistrationNumber,
            breeder: {
              name: horseShortInfo.breederName
            },
            owner: {
              fullName: horseShortInfo.ownerName
            }
          }
        } as racehorse360.IStableEntry
      };
      setStateStablingEntries(newValue);
      selectAndCloseSearch(newValue);
    };

  const handleUnselectClick = (horseId: string) => e => {
    e.stopPropagation();
    const newStateStablingEntries = {
      ...stateStablingEntries,
      [horseId]: undefined
    };
    setStateStablingEntries(newStateStablingEntries);
    onChange(Object.values(newStateStablingEntries).filter(Boolean));
  };

  const handleDeleteEntryClick = (horseId: string) => () => {
    const newStateStablingEntries = {
      ...stateStablingEntries,
      [horseId]: undefined
    };
    setStateStablingEntries(newStateStablingEntries);
    onChange(Object.values(newStateStablingEntries).filter(Boolean));
  };

  const handleReadyDateChange = (
    stablingEntry: racehorse360.IStableEntry,
    date: Date
  ) => {
    const newStateStableingEntries = {
      ...stateStablingEntries,
      [stablingEntry.horseId]: {
        ...stablingEntry,
        raceReadyDate: date
          ? formatISO(date, {
              representation: "date"
            })
          : EMPTY_STRING
      }
    };
    setStateStablingEntries(newStateStableingEntries);
    onChange(Object.values(newStateStableingEntries).filter(Boolean));
  };

  const handleSearchClose = () => {
    setSearchQuery("");
    setShowResults(false);
  };

  const selectAndCloseSearch = (newValue: StableEntriesMap) => {
    onChange(Object.values(newValue).filter(Boolean));
    handleSearchClose();
  };

  const handleDateClick = entry => () => {
    setActiveStablingEntry(entry);
    toggleDatePopupOpen();
  };

  const handleCollapseModeToggle = () => {
    setIsCollapseMode(!isCollapseMode);
  };

  const handleAddCustomHorseClick = () => {
    setIsOpenEntryModal(true);
  };

  useEffect(() => {
    if (!isListStableEntriesFetching) {
      setStateStablingEntries(
        mapStablingEntries(pickedStableEntries || originalStableEntries)
      );
    }
  }, [isListStableEntriesFetching, pickedStableEntries, originalStableEntries]);

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.titleWrapper}>
          <span className={classes.title}>Stabling Entries</span>
          <span className={classes.count}>
            My Entries: {stablingEntries.length}
          </span>
        </div>
        <div className={classes.search}>
          <TextField
            className={classes.searchInputBase}
            placeholder={"Horse Name or Reg. #"}
            name={STABLE_ENTRIES_INPUT}
            value={searchQuery}
            onChange={handleSearchQueryChange}
            InputProps={{
              classes: {
                input: classes.searchInput
              },
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon className={classes.searchIcon} />
                </InputAdornment>
              ),
              endAdornment: showResults ? (
                <IconButton size="small" onClick={handleSearchClose}>
                  <CloseIcon className={classes.clearSearchIcon} />
                </IconButton>
              ) : null
            }}
            onFocus={handleSearchFocus}
            variant="outlined"
            error={hasEntriesValidationError}
          />
          <Button
            className={clsx(classes.searchHamburgerButton, {
              [classes.searchHamburgerButtonActive]: isCollapseMode
            })}
            onClick={handleCollapseModeToggle}
          >
            <ReorderIcon className={classes.searchHamburgerIcon} />
          </Button>
        </div>
      </div>
      <div
        className={clsx(
          classes.body,
          showResults ? classes.searchResults : classes.entries
        )}
      >
        {isDatePopupOpen && (
          <DatePopup
            stablingEntry={activeStablingEntry}
            isOpen={isDatePopupOpen}
            onClose={toggleDatePopupOpen}
            onChange={handleReadyDateChange}
          />
        )}
        {(isLoading || isListStableEntriesFetching) && <Loader overlay />}
        <div className={classes.resultsWrapper}>
          {hasEntriesValidationError && (
            <div
              className={clsx(classes.validationError, {
                withResults: showResults
              })}
            >
              <InvalidIcon className={classes.validationIcon} />
              {STABLE_ENTRIES_ERROR}
            </div>
          )}
          {showResults && searchQuery ? (
            <>
              {horses.map(horseShortInfo => (
                <SummaryInfo
                  key={horseShortInfo.id}
                  selected={Boolean(stateStablingEntries[horseShortInfo.id])}
                  mainTitle={horseShortInfo.horseName}
                  contentNumber={horseShortInfo.horseRegistrationNumber}
                  contentText={
                    horseShortInfo.ownerName ||
                    horseShortInfo.breederName ||
                    DASH
                  }
                  onItemSelect={handleHorseClick(horseShortInfo)}
                  onItemUnselect={handleUnselectClick(horseShortInfo.id)}
                  classes={{
                    root: classes.summaryInfoRoot,
                    info: classes.summaryItemInfo
                  }}
                />
              ))}
              <Button
                className={clsx(
                  classes.searchResultsEntry,
                  classes.searchResultsEntryAddButton
                )}
                onClick={handleAddCustomHorseClick}
              >
                <AddCircleOutlineIcon
                  className={classes.searchResultsEntryButtonIcon}
                />
                Add Unlisted Horse
              </Button>
              {isOpenEntryModal && (
                <AddUnlistedHorsePopup
                  setIsOpenEntryModal={setIsOpenEntryModal}
                  searchQuery={searchQuery}
                  onAddHorseClick={onAddHorseClick}
                />
              )}
              {!isLoading &&
                debouncedSearchQuery.length > 2 &&
                !horses.length && (
                  <div className={classes.searchResultsNoResults}>
                    No Results for "{debouncedSearchQuery}"
                  </div>
                )}
            </>
          ) : (
            stablingEntries.map(entry => (
              <Paper key={entry.horseId} className={classes.entry}>
                <div className={classes.entryBody}>
                  <ul className={classes.entryBodyList}>
                    <li className={classes.entryTitle}>
                      {entry.horse?.name || entry.customHorseName}
                    </li>
                    {!isCollapseMode && (
                      <>
                        <li className={classes.entrySubtitle}>
                          {entry.horse?.registrationNumber ||
                            entry.customHorseRegistrationNumber}
                        </li>
                        <li className={classes.entrySubtitle}>
                          {entry.horse?.owner?.fullName ||
                            entry.horse?.breeder?.name ||
                            DASH}
                        </li>
                      </>
                    )}
                    <li>
                      <Button
                        className={clsx(classes.entryAddDateButton, {
                          [classes.entryEditDateButton]: entry.raceReadyDate
                        })}
                        startIcon={
                          entry.raceReadyDate ? (
                            <EditIcon
                              className={classes.entryAddDateButtonIcon}
                            />
                          ) : (
                            <CalendarAdd
                              className={classes.entryAddDateButtonIcon}
                            />
                          )
                        }
                        onClick={handleDateClick(entry)}
                      >
                        {entry.raceReadyDate
                          ? format(parseISO(entry.raceReadyDate), "MM/dd/yy")
                          : "Add race ready date"}
                      </Button>
                    </li>
                  </ul>
                  <IconButton
                    className={classes.entryRemoveButton}
                    onClick={handleDeleteEntryClick(entry.horseId)}
                    size={"small"}
                  >
                    <CancelIcon />
                  </IconButton>
                </div>
              </Paper>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(StablingEntries);
